// Parse json.comments (one entry per newline, newest first — writers prepend).
// Handles three shapes seen live on dev:
//   <ts> : <author> : <text>   — human (ApprovalManager.prepend_comment)
//   <ts> : INTERNAL: <text>    — machine audit
//   <text>                     — legacy free-form editor text (no ts/author)
// A free-form entry can span multiple lines (a textarea note); consecutive
// unstructured lines fold into one row. Consecutive rows with the same
// (author, text) collapse to one with `count` — the newest ts wins.

// The comma is optional: invoice.js formats with Intl.DateTimeFormat, which
// omits it under a French locale.
const TS_ANCHOR = /^(\d{2}\/\d{2}\/\d{4},? \d{2}:\d{2}:\d{2})\s*:\s*(.*)$/;

export function parseComments(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  const rows = [];
  let freeFormBuffer = null;
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const parsed = parseOne(trimmed);
    if (parsed.timestamp) {
      if (freeFormBuffer) { rows.push(freeFormBuffer); freeFormBuffer = null; }
      rows.push(parsed);
    } else if (freeFormBuffer) {
      freeFormBuffer.text += `\n${parsed.text}`;
    } else {
      freeFormBuffer = parsed;
    }
  }
  if (freeFormBuffer) rows.push(freeFormBuffer);
  return collapse(rows);
}

function parseOne(line) {
  const anchored = line.match(TS_ANCHOR);
  if (!anchored) return { timestamp: '', author: '', text: line };
  const [, timestamp, rest] = anchored;
  if (rest.startsWith('INTERNAL:')) {
    return { timestamp, author: 'INTERNAL', text: rest.slice('INTERNAL:'.length).trim() };
  }
  const authorSep = rest.indexOf(' : ');
  if (authorSep < 0) return { timestamp: '', author: '', text: line };
  return {
    timestamp,
    author: rest.slice(0, authorSep).trim(),
    text: rest.slice(authorSep + 3).trim(),
  };
}

function collapse(rows) {
  const out = [];
  for (const row of rows) {
    const last = out[out.length - 1];
    if (last && last.author === row.author && last.text === row.text) {
      last.count += 1;
      // Newest wins — the input is newest-first, so `last` (the earlier
      // iteration) already holds the newest timestamp; do not overwrite.
    } else {
      out.push({ ...row, count: 1 });
    }
  }
  return out;
}

// 140 clears the longest note the API builds (a French skipped-conversion
// line with the English fallback reason, 117 chars) without letting a
// pasted free-form block run away.
export function summarizeNewest(rows, { internalLabel = '', maxChars = 140 } = {}) {
  const newest = rows[0];
  if (!newest) return '';
  const author = newest.author === 'INTERNAL' && internalLabel ? internalLabel : newest.author;
  const label = author ? `${author} · ` : '';
  const combined = `${label}${newest.text}`;
  if (combined.length <= maxChars) return combined;
  return `${combined.slice(0, maxChars - 1)}…`;
}
